/**
 * RelinkLayerDialog — shown when a layer's data endpoint is unreachable.
 *
 * Lets the user provide a new URL or local path for the layer's artifact,
 * then patches the scene item's endpoints in the gTwin via the platform API.
 */

import { useCallback, useState } from "react";
import { Icon, tokens } from "@/design";

export interface UnreachableLayer {
  readonly layerId: string;
  readonly layerLabel: string;
  readonly endpointKey: string;
  readonly currentUrl: string;
  readonly error: string;
}

interface Props {
  readonly layers: readonly UnreachableLayer[];
  readonly gtwinId: string;
  readonly onRelink: (layerId: string, endpointKey: string, newUrl: string) => void;
  readonly onDismiss: () => void;
}

export function RelinkLayerDialog({ layers, gtwinId: _gtwinId, onRelink, onDismiss }: Props) {
  const [edits, setEdits] = useState<Record<string, string>>({});

  const handleRelink = useCallback(
    (layer: UnreachableLayer) => {
      const newUrl = edits[layer.layerId]?.trim();
      if (newUrl) {
        onRelink(layer.layerId, layer.endpointKey, newUrl);
      }
    },
    [edits, onRelink],
  );

  if (layers.length === 0) return null;

  return (
    <div
      style={{
        margin: tokens.space[3],
        padding: tokens.space[3],
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[2],
        border: tokens.border.thin,
        fontSize: tokens.font.size.xs,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: tokens.space[2], marginBottom: tokens.space[2] }}>
        <Icon name="link_off" size={20} decorative />
        <strong style={{ fontSize: tokens.font.size.sm }}>
          {layers.length} layer{layers.length === 1 ? "" : "s"} unreachable
        </strong>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            marginLeft: "auto",
            background: "none",
            border: "none",
            color: tokens.color.ink.secondary,
            cursor: "pointer",
            fontSize: tokens.font.size.xs,
          }}
        >
          Dismiss
        </button>
      </div>
      <p style={{ margin: `0 0 ${tokens.space[2]}px`, opacity: 0.7 }}>
        These layers point to files that can't be reached. Enter a new path or URL to re-link them.
      </p>
      {layers.map((layer) => (
        <div
          key={layer.layerId}
          style={{
            padding: tokens.space[2],
            marginBottom: tokens.space[2],
            borderRadius: tokens.radius.sm,
            background: tokens.color.surface[1],
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{layer.layerLabel}</div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>
            Current: <code>{layer.currentUrl}</code>
          </div>
          <div style={{ fontSize: 11, color: tokens.color.semantic.danger, marginBottom: 6 }}>
            {layer.error}
          </div>
          <div style={{ display: "flex", gap: tokens.space[2] }}>
            <input
              value={edits[layer.layerId] ?? ""}
              onChange={(e) => setEdits({ ...edits, [layer.layerId]: e.target.value })}
              placeholder="New URL or path..."
              style={{
                flex: 1,
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                borderRadius: tokens.radius.input,
                border: tokens.border.thin,
                background: tokens.color.surface[0],
                color: "inherit",
                fontSize: 12,
                fontFamily: "monospace",
              }}
            />
            <button
              type="button"
              onClick={() => handleRelink(layer)}
              disabled={!edits[layer.layerId]?.trim()}
              style={{
                padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                borderRadius: tokens.radius.input,
                border: tokens.border.thin,
                background: edits[layer.layerId]?.trim() ? tokens.color.accent.primary : tokens.color.surface[2],
                color: edits[layer.layerId]?.trim() ? tokens.color.accent.on : tokens.color.ink.secondary,
                cursor: edits[layer.layerId]?.trim() ? "pointer" : "not-allowed",
                fontSize: 12,
              }}
            >
              Re-link
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
